/**
 * Shared watermark tag helpers.
 *
 * A watermark tag is a single mutually exclusive setting stored on
 * `ImageData.watermarkTag` ('none' | 'actual-photo' | 'ai-generated' |
 * 'ai-assisted'). Variations that can place the tag at either end of the
 * image keep that choice separately, in `ImageData.watermarkPosition`.
 */
import ActualWatermark from '@/assets/icons/cover-images/actual-photo-watermark.png';
import AiAssistedTag from '@/assets/icons/cover-images/ai-assisted-tag.png';
import AiGeneratedTag from '@/assets/icons/cover-images/ai-generated-tag.png';

import type { SettingSectionConfig } from './SettingsModal';

export type WatermarkKind = 'actual-photo' | 'ai-generated' | 'ai-assisted';

export type WatermarkPosition = 'bottom' | 'top';

export const WATERMARK_TAG_SRC: Record<WatermarkKind, string> = {
  'actual-photo': ActualWatermark,
  'ai-generated': AiGeneratedTag,
  'ai-assisted': AiAssistedTag,
};

/** Watermark options, without a position — the kinds on their own */
export const WATERMARK_TAGS = [
  { label: 'None', value: 'none' },
  { label: 'Not The Actual Photo', value: 'actual-photo' },
  { label: 'AI-Generated', value: 'ai-generated' },
  { label: 'AI-Assisted', value: 'ai-assisted' },
];

export const WATERMARK_POSITIONS = ['bottom', 'top'];

interface WatermarkImage {
  watermarkTag?: string;
  watermarkPosition?: string;
}

export const getWatermarkTag = (image: WatermarkImage) =>
  image.watermarkTag ?? 'none';

export const getWatermarkPosition = (image: WatermarkImage) =>
  image.watermarkPosition ?? 'bottom';

/**
 * Fills in the watermark defaults so the settings modal radios start out
 * checked for images that have never had a watermark set.
 */
export const withWatermarkDefaults = <T extends WatermarkImage>(
  image: T,
): T => ({
  ...image,
  watermarkTag: getWatermarkTag(image),
  watermarkPosition: getWatermarkPosition(image),
});

/** Watermark sections for the shared SettingsModal */
export const WATERMARK_SECTIONS: SettingSectionConfig[] = [
  {
    title: 'Watermark',
    type: 'radio',
    stateKey: 'watermarkTag',
    options: WATERMARK_TAGS,
    columns: 2,
  },
  {
    title: 'Watermark Position',
    type: 'radio',
    stateKey: 'watermarkPosition',
    options: WATERMARK_POSITIONS,
  },
];

/** For the templates that only ever draw the watermark at the bottom */
export const WATERMARK_SECTIONS_NO_POSITION: SettingSectionConfig[] = [
  WATERMARK_SECTIONS[0],
];

export interface ResolvedWatermarkTag {
  kind: WatermarkKind;
  position: WatermarkPosition;
  src: string;
}

/**
 * Builds the watermark image for a generated cover, or null when the image
 * has no watermark. Keeps each template's own base class — the AI tags reuse
 * the position the actual photo watermark already has — and adds a modifier
 * per kind (`${baseClassName}--ai-generated`) as a hook for templates that
 * need to nudge them.
 */
export const createWatermarkTagImage = (
  baseClassName: string,
  tag?: string,
  position?: string,
): HTMLImageElement | null => {
  const watermark = resolveWatermarkTag(tag, position);
  if (!watermark) return null;

  const watermarkImage = new Image();
  watermarkImage.classList.add(baseClassName);
  if (watermark.kind !== 'actual-photo') {
    watermarkImage.classList.add(`${baseClassName}--${watermark.kind}`);
  }
  if (watermark.position === 'top') {
    watermarkImage.classList.add(`${baseClassName}--top`);
  }
  watermarkImage.src = watermark.src;

  return watermarkImage;
};

/** Returns null for 'none', an unset value or anything unrecognised */
export const resolveWatermarkTag = (
  tag?: string,
  position?: string,
): ResolvedWatermarkTag | null => {
  if (!tag || !(tag in WATERMARK_TAG_SRC)) return null;

  const kind = tag as WatermarkKind;

  return {
    kind,
    position: position === 'top' ? 'top' : 'bottom',
    src: WATERMARK_TAG_SRC[kind],
  };
};
